import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { ElementType, PropOrElemType } from 'src/app/_objects/element-type';
import { PropertyPanelDef } from 'src/app/_objects/interfaces/property-panel';
import { ReportProperties } from 'src/app/_objects/report-properties';
import {
  Notifier,
  PropertyPreviewMetadata,
  ReportTemplateDesignService,
  UpdateMetadata,
} from 'src/app/_services/report-template-design.service';

@Component({
    selector: 'app-property-new-preview',
    templateUrl: './property-new-preview.component.html',
    styleUrls: ['./property-new-preview.component.scss'],
    standalone: false
})
export class PropertyNewPreviewComponent implements OnDestroy {
  public propertyTableColumns = ['property'];
  public commonPropPanels: PropertyPanelDef[];
  public selectedPropPanels: PropertyPanelDef[];

  private mySubscriber: Subscription[];
  private notifier: Notifier;
  @Output() public closePropertyPanel = new EventEmitter<any>();
  @Output() public closeSnackBar = new EventEmitter<any>();

  public permissionForActions: { [actionName: string]: boolean };

  constructor(private designService: ReportTemplateDesignService) {
    this.mySubscriber = [];
    this.notifier = {
      componentName: 'property-preview.component',
      ids: { pageId: '', elementId: '' },
    };

    this.permissionForActions = {
      save: true,
    };

    this.mySubscriber.push(
      this.designService.onReady.subscribe((message: string) => {
        this.viewCommonElement();
        this.viewSelectedElement();
      })
    );

    this.mySubscriber.push(
      this.designService.onEndUpdate.subscribe((message: string) => {
        this.viewCommonElement();
      })
    );

    this.mySubscriber.push(
      this.designService.onSelectedElement.subscribe((message: string) => {
        this.viewSelectedElement();
      })
    );
  }

  public ngOnDestroy() {
    this.mySubscriber.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  public closePanel(): void {
    this.closePropertyPanel.emit();
  }

  public viewSelectedElement() {
    const selectedElemProps: ReportProperties[] = this.designService.getSelectedElementProperties();
    const application = this.getApplication();
    this.selectedPropPanels = [];
    selectedElemProps.forEach((reportProperties: ReportProperties) => {
      if (this.getPanelType(reportProperties.getPropOrElemType()) === 'Page') {
        this.selectedPropPanels.push({
          propId: reportProperties.getId(),
          title: reportProperties.getTitle(),
          propOrElemType: reportProperties.getPropOrElemType(),
          mapTableData: reportProperties.getMapProperties(),
        });
      }

      if (this.getPanelType(reportProperties.getPropOrElemType()) !== 'Page') {
        if (application === 'ViSOL') {
          this.selectedPropPanels.push({
            propId: reportProperties.getId(),
            title: reportProperties.getTitle(),
            propOrElemType: reportProperties.getPropOrElemType(),
            mapTableData: reportProperties.getMapProperties(),
          });
        } else if (application === 'IPM') {
          this.selectedPropPanels.push({
            propId: reportProperties.getId(),
            title: reportProperties.getTitle(),
            propOrElemType: reportProperties.getPropOrElemType(),
            mapTableData: reportProperties.getMapProperties(),
          });
        } else {
          this.selectedPropPanels.push({
            propId: reportProperties.getId(),
            title: reportProperties.getTitle(),
            propOrElemType: reportProperties.getPropOrElemType(),
            mapTableData: reportProperties.getMapProperties(),
          });
        }
      }
    });
    this.selectedPropPanels =
      this.selectedPropPanels.length > 1
        ? this.selectedPropPanels.filter(x => x.propOrElemType !== ElementType.Page)
        : this.selectedPropPanels;
  }
  public viewCommonElement() {
    const commProps: ReportProperties[] = this.designService.getCommonProperties();
    this.commonPropPanels = [];
    commProps.forEach((reportProperties: ReportProperties) => {
      this.commonPropPanels.push({
        propId: reportProperties.getId(),
        title: reportProperties.getTitle(),
        propOrElemType: reportProperties.getPropOrElemType(),
        mapTableData: reportProperties.getMapProperties(),
      });
    });
  }
  public onCommPropChange(data: UpdateMetadata) {
    this.designService.notifyUpdateCommonProperties(this.notifier, data);
  }

  public onElemPropChange(data: UpdateMetadata) {
    const template = { ...this.designService.originTemplate };
    template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
    this.designService.templateForUndo.push(template);
    this.designService.notifyUpdateElementProperties(this.notifier, data);
    if(data.name === 'hasHeader' || data.name === 'hasFooter') {
      this.designService.reloadReportTemplate(this.designService.getTempTemplate(), true);
    }
  }
  public getPanelType(propOrElemType: PropOrElemType): string {
    const types: ElementType[] = [
      ElementType.Page,
      ElementType.Text,
      ElementType.Line,
      ElementType.Image,
      ElementType.Rectangle,
      ElementType.Summary,
      ElementType.Picture,
      ElementType.DataPlate,
      ElementType.MeasurementPoint,
      ElementType.Signature,
      ElementType.VisualEvents,
      ElementType.Table,
      ElementType.DropDownList,
      ElementType.InteractiveIso,
      ElementType.Section,
    ];
    for (const type of types) {
      if (propOrElemType.indexOf(type) === 0) {
        return type;
      }
    }
    return null;
  }

  public isVisolTable(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type === ElementType.Summary || type === ElementType.SubTotal || type === ElementType.Picture || type === ElementType.DataPlate;
  }
  public isGeneral(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type !== ElementType.SubTotal && type !== ElementType.DataPlate;
  }
  public isTitle(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type === ElementType.Summary || type === ElementType.SubTotal || type === ElementType.Picture || type === ElementType.DataPlate;
  }
  public isTable(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type === ElementType.Summary || type === ElementType.SubTotal || type === ElementType.Picture;

  }
  public isDataPlate(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type === ElementType.DataPlate;
  }
  public activeTab(propOrElemType: PropOrElemType, selected: string): string {
    const type = this.getPanelType(propOrElemType);
    switch (selected) {
      case 'Table':
        if (type === ElementType.DataPlate) {
          return 'Title';
        } else {
          return selected;
        }
      case 'ImageInfo':
        if (type === ElementType.DataPlate) {
          return selected;
        } else if (type === ElementType.SubTotal) {
          return 'Title';
        } else {
          return 'General';
        }
      case 'General':
      case 'Title':
      default:
        if (type === ElementType.DataPlate || type === ElementType.SubTotal) {
          return 'Title';
        } else {
          return selected ? selected : 'General';
        }
    }
  }

  public getType(previewData: PropertyPreviewMetadata[]): string {
    let type: string = null;
    previewData.forEach(metadata => {
      if (metadata.name === 'type') {
        type = metadata.value as string;
        return;
      }
    });
    return type;
  }
  public getInputType(previewData: PropertyPreviewMetadata[]): string {
    let inputType: string = null;
    previewData.forEach(metadata => {
      if (metadata.name === 'inputType') {
        inputType = metadata.value as string;
        return;
      }
    });
    return inputType;
  }
  public getDynamicId(previewData: PropertyPreviewMetadata[]): string {
    let dynamicId: string = null;
    previewData.forEach(metadata => {
      if (metadata.name === 'dynamicId') {
        dynamicId = metadata.value as string;
        return;
      }
    });
    return dynamicId;
  }
  public getMaxLength(previewData: PropertyPreviewMetadata[]): number {
    let maxLength: number = null;
    previewData.forEach(metadata => {
      if (metadata.name === 'maxLength') {
        maxLength = metadata.value as number;
        return;
      }
    });
    return maxLength;
  }
  public isLayoutData(previewData: PropertyPreviewMetadata, panelType: string): boolean {
    const application = this.getApplication();
    if (application === 'ViSOL') {
      return (
        0 > ReportTemplateDesignService.PROP_DYNAMIC_ATTR4VISOL.indexOf(previewData.name) &&
        0 > ReportTemplateDesignService.PROP_DYNAMIC_ATTR4IPM.indexOf(previewData.name)
      );
    } else if (application === 'IPM') {
      return (
        0 > ReportTemplateDesignService.PROP_DYNAMIC_ATTR4IPM.indexOf(previewData.name) &&
        0 > (panelType === 'Text' ? ['dynamicId', 'isHorizontal'] : ['dynamicId']).indexOf(previewData.name)
      );
    } else {
      return panelType === 'Text'
        ? previewData.group !== undefined && 0 > ['isHorizontal'].indexOf(previewData.name)
        : previewData.group !== undefined;
    }
  }
  public isDynamicData(previewData: PropertyPreviewMetadata, panelType: string): boolean {
    const application = this.getApplication();
    if (panelType === 'Text') {
      if (application === 'ViSOL') {
        return 0 <= ReportTemplateDesignService.PROP_DYNAMIC_ATTR4VISOL.indexOf(previewData.name);
      } else if (application === 'IPM') {
        return 0 <= ReportTemplateDesignService.PROP_DYNAMIC_ATTR4IPM.indexOf(previewData.name);
      } else {
        return previewData.group === undefined && 'dynamicId' !== previewData.name;
      }
    } else {
      // TODO: Delete when ViSOL Image corresponds to DynamicId
      if (application === 'ViSOL' && panelType === 'Image' && previewData.name === 'type') {
        previewData.selectItems = previewData.selectItems.filter(_ => _.label === 'static');
      }
      return !this.isLayoutData(previewData, panelType) && previewData.group === undefined;
    }
  }
  public isDisplay(propertyPanelDef: PropertyPanelDef): boolean {
    let flag = false;
    if (propertyPanelDef.mapTableData === undefined) {
      return false;
    }
    propertyPanelDef.mapTableData.forEach(previewDatas => {
      previewDatas.forEach(previewData => {
        flag = flag || this.isDynamicData(previewData, propertyPanelDef.propOrElemType);
      });
    });
    return flag;
  }
  public asIsOrder(a, b) {
    return 1;
  }
  public getApplication(): string {
    return this.designService.getTemplateApplication();
  }
}
